import React, { useContext, useReducer, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { mediaUp } from '@one/theme-utils';
import { NavigationContext } from '../../context/NavigationContext';

const defaultStyles = {
    top: 0,
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    lineHeight: 1,
    textDecoration: 'none',
    zIndex: 102, // catfish 101 < Nav < PPP bar 500
};

const typeStyles = {
    top: {
        top: '0',
        width: '100%',
        right: '0',
        left: '0',
        transform: 'rotate(360deg)',
    },
    left: {
        position: 'fixed !important',
        left: '0',
        right: 'auto',
        height: '100vh',
        transform: 'translateX(0%)',
    },
    right: {
        position: 'fixed !important',
        right: '0',
        height: '100vh',
        left: 'auto',
        transform: 'translateX(0%)',
    },
    bottom: {
        position: 'fixed !important',
        bottom: '0',
        width: '100%',
        top: 'auto',
    },
};

const Nav = styled.nav`
    ${({ structureType }) => {
        if (structureType === 'mobile') {
            return `
                right: 0;
                left: 0;
                transform: rotate(360deg);
            `;
        }

        return `
            left: 50%;
            transform: translateX(-50%);
        `;
    }}

    ${({ isContentFullWidth, theme: { grid: { gutter } = {} } = {} }) =>
        isContentFullWidth
            ? mediaUp('medium')`
        padding: 0 calc(0.5 * ${gutter});
    `
            : ''}

    transition: background 0.5s, background-color 0.5s;

    ${({ widthOverrides }) => mediaUp('small')`max-width: ${widthOverrides.small};`};

    ${({ styles = {} }) => styles}
`;

const initReducer = state => {
    const {
        nav,
        initValues: { background, backgroundColor, toggleOnScroll },
    } = state;

    const parsedBackground = background && background.replace(';', '');
    const parsedBackgroundColor = backgroundColor && backgroundColor.replace(';', '');

    return {
        ...state,
        nav: {
            ...nav,
            background: background ? parsedBackground : parsedBackgroundColor,
        },
        initValues: {
            ...state.initValues,
            toggleOnScroll: background && toggleOnScroll,
            background: parsedBackground,
            backgroundColor: parsedBackgroundColor,
        },
    };
};

const getNavZIndex = alignment => {
    // Top > Right & Left > Bottom
    switch (alignment) {
        case 'top':
            return 104;
        case 'bottom':
            return 102;
        default:
            return 103;
    }
};

const stateReducer = (
    { nav, initValues, ...state },
    { isSticky, alignment },
) => {
    if (isSticky) {
        const { background: initBackground, backgroundColor, toggleOnScroll } = initValues;

        return {
            ...state,
            nav: {
                ...nav,
                position: 'fixed',
                background: toggleOnScroll ? backgroundColor : initBackground,
                zIndex: getNavZIndex(alignment),
            },
            initValues,
        };
    }

    return { ...state, nav, initValues };
};

const NavComponent = ({
    styles: { position = 'relative', background, backgroundColor, ...styles } = {},
    isContentPushed,
    isSticky,
    toggleOnScroll,
    isContentFullWidth,
    structureType,
    widthOverrides,
    alignment,
    children,
}) => {
    const { setNavAlignment } = useContext(NavigationContext);
    const [styleState, setStyleState] = useReducer(
        stateReducer,
        {
            nav: { position },
            initValues: { background, backgroundColor, toggleOnScroll },
        },
        initReducer,
    );

    const { state: { clientWidth = 0 } = {} } = useContext(NavigationContext);
    const navRef = useRef();
    const [navHeight, setNavHeight] = useState(0);

    useEffect(() => {
        setNavAlignment(alignment);
        setStyleState({
            isSticky,
            alignment,
        });
    }, [isSticky, alignment]);

    // Monitor nav height changes with ResizeObserver
    useEffect(() => {
        if (!navRef.current) return () => {};

        const updateHeight = () => {
            const height = navRef.current?.clientHeight || 0;
            setNavHeight(height);
        };

        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(navRef.current);

        return () => resizeObserver.unobserve(navRef.current);
    }, []);

    const width = structureType !== 'mobile' && clientWidth;
    const { nav } = styleState;
    let marginBottom = 0;

    if (isSticky) {
        if (isContentPushed) {
            marginBottom = navHeight;
        } else {
            marginBottom = -navHeight;
        }
    }

    const wrapperStyles = {
        paddingBottom: isSticky && !isContentPushed ? navHeight : 0,
        marginBottom,
    };

    return (
        <div style={wrapperStyles}>
            <Nav
                ref={navRef}
                style={{ ...nav, width }}
                styles={{ ...defaultStyles, ...typeStyles[alignment], ...styles }}
                isContentFullWidth={isContentFullWidth}
                structureType={structureType}
                widthOverrides={widthOverrides}
                className={`nav-component ${alignment}`}
            >
                {children}
            </Nav>
        </div>
    );
};

NavComponent.propTypes = {
    styles: PropTypes.object,
    isContentPushed: PropTypes.bool,
    isContentFullWidth: PropTypes.bool,
    isSticky: PropTypes.bool,
    structureType: PropTypes.string,
    toggleOnScroll: PropTypes.bool,
    children: PropTypes.node,
    widthOverrides: PropTypes.object,
    alignment: PropTypes.string,
};

export default NavComponent;
